/**
 * Spot Helper Functions for Egress Blocking Calculation
 * Calculates which spots block other spots during egress based on spatial geometry
 */

import { Point, Shape, SpotMetadata } from "./RampCanvas";

/**
 * Log entry for a single spot's egress calculation
 */
export type EgressCalculationLog = {
  spotId: string;
  spotName: string;
  egressLane?: string;
  hasEgressLane: boolean;
  blocksSpots: string[]; // Spot IDs this spot blocks
  skipped: boolean;
  reason: string; // "No egress lane" | "Lane not found" | "Calculated"
  intersectionCount: number; // Number of spots checked for intersection
};

/**
 * Result of egress blocking calculation with logs and statistics
 */
export type EgressCalculationResult = {
  shapes: Shape[];
  logs: EgressCalculationLog[];
  totalSpots: number;
  spotsWithEgress: number;
  spotsWithoutEgress: number;
  totalBlockingRelationships: number;
};

/**
 * Calculate the center point of a spot's bounding box
 */
export function getSpotCenter(spot: Shape): Point {
  if (!spot.points || spot.points.length === 0) {
    return { x: 0, y: 0 };
  }

  const xs = spot.points.map((p) => p.x);
  const ys = spot.points.map((p) => p.y);

  return {
    x: (Math.min(...xs) + Math.max(...xs)) / 2,
    y: (Math.min(...ys) + Math.max(...ys)) / 2,
  };
}

/**
 * Get the maximum width of a spot's bounding box
 * Used to determine corridor width for egress path
 */
export function getSpotMaxWidth(spot: Shape): number {
  if (!spot.points || spot.points.length === 0) {
    return 0;
  }

  const xs = spot.points.map((p) => p.x);
  const ys = spot.points.map((p) => p.y);

  const width = Math.max(...xs) - Math.min(...xs);
  const height = Math.max(...ys) - Math.min(...ys);

  return Math.max(width, height);
}

/**
 * Find the nearest point on a polyline to a given point
 * @param point - The point to project
 * @param linePoints - Points defining the polyline
 * @returns The nearest point on the polyline
 */
export function nearestPointOnPolyline(
  point: Point,
  linePoints: Point[]
): Point {
  if (linePoints.length === 0) {
    return point;
  }

  if (linePoints.length === 1) {
    return linePoints[0];
  }

  let nearestPoint = linePoints[0];
  let minDistance = Infinity;

  // Check each line segment
  for (let i = 0; i < linePoints.length - 1; i++) {
    const segmentStart = linePoints[i];
    const segmentEnd = linePoints[i + 1];
    const nearest = nearestPointOnSegment(point, segmentStart, segmentEnd);
    const distance = distanceBetweenPoints(point, nearest);

    if (distance < minDistance) {
      minDistance = distance;
      nearestPoint = nearest;
    }
  }

  return nearestPoint;
}

/**
 * Find the nearest point on a line segment to a given point
 */
function nearestPointOnSegment(
  point: Point,
  segmentStart: Point,
  segmentEnd: Point
): Point {
  const dx = segmentEnd.x - segmentStart.x;
  const dy = segmentEnd.y - segmentStart.y;

  // If segment is actually a point
  if (dx === 0 && dy === 0) {
    return segmentStart;
  }

  // Calculate parameter t for point projection
  const t = Math.max(
    0,
    Math.min(
      1,
      ((point.x - segmentStart.x) * dx + (point.y - segmentStart.y) * dy) /
        (dx * dx + dy * dy)
    )
  );

  return {
    x: segmentStart.x + t * dx,
    y: segmentStart.y + t * dy,
  };
}

/**
 * Calculate distance between two points
 */
function distanceBetweenPoints(p1: Point, p2: Point): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Check if a point is inside a polygon using ray casting algorithm
 * @param point - The point to test
 * @param polygon - Array of points defining the polygon
 * @returns true if point is inside polygon, false otherwise
 */
export function pointInPolygon(point: Point, polygon: Point[]): boolean {
  if (polygon.length < 3) {
    return false;
  }

  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;

    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Create a corridor (rectangle) from start point to end point with given width
 * The corridor represents the egress path a spot needs to reach its egress lane
 * @param start - Spot center point
 * @param end - Nearest point on egress lane
 * @param width - Width of corridor (spot's max dimension)
 * @returns Array of 4 points defining the corridor rectangle
 */
export function createCorridor(
  start: Point,
  end: Point,
  width: number
): Point[] {
  // Calculate direction vector
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.sqrt(dx * dx + dy * dy);

  // Handle case where start and end are the same
  if (length === 0) {
    const halfWidth = width / 2;
    return [
      { x: start.x - halfWidth, y: start.y - halfWidth },
      { x: start.x + halfWidth, y: start.y - halfWidth },
      { x: start.x + halfWidth, y: start.y + halfWidth },
      { x: start.x - halfWidth, y: start.y + halfWidth },
    ];
  }

  // Normalize direction vector
  const ux = dx / length;
  const uy = dy / length;

  // Perpendicular vector (rotated 90 degrees)
  const px = -uy;
  const py = ux;

  // Half width offset
  const halfWidth = width / 2;

  // Create rectangle corners
  return [
    { x: start.x + px * halfWidth, y: start.y + py * halfWidth },
    { x: end.x + px * halfWidth, y: end.y + py * halfWidth },
    { x: end.x - px * halfWidth, y: end.y - py * halfWidth },
    { x: start.x - px * halfWidth, y: start.y - py * halfWidth },
  ];
}

/**
 * Check if two polygons intersect
 * Uses Separating Axis Theorem (SAT) for convex polygons
 */
export function polygonsIntersect(poly1: Point[], poly2: Point[]): boolean {
  // Edge case: empty polygons
  if (poly1.length < 3 || poly2.length < 3) {
    return false;
  }

  // Test all edges from both polygons as potential separating axes
  return !isSeparated(poly1, poly2) && !isSeparated(poly2, poly1);
}

/**
 * Check if there's a separating axis between two polygons
 * If we find a separating axis, polygons don't intersect
 */
function isSeparated(poly1: Point[], poly2: Point[]): boolean {
  for (let i = 0; i < poly1.length; i++) {
    const p1 = poly1[i];
    const p2 = poly1[(i + 1) % poly1.length];

    // Get edge vector and perpendicular (normal)
    const edge = { x: p2.x - p1.x, y: p2.y - p1.y };
    const normal = { x: -edge.y, y: edge.x };

    // Project both polygons onto this axis
    const [min1, max1] = projectPolygon(poly1, normal);
    const [min2, max2] = projectPolygon(poly2, normal);

    // Check for gap (separation)
    if (max1 < min2 || max2 < min1) {
      return true; // Found separating axis
    }
  }

  return false; // No separating axis found
}

/**
 * Project a polygon onto an axis and return min/max values
 */
function projectPolygon(polygon: Point[], axis: Point): [number, number] {
  let min = Infinity;
  let max = -Infinity;

  for (const point of polygon) {
    const projection = point.x * axis.x + point.y * axis.y;
    min = Math.min(min, projection);
    max = Math.max(max, projection);
  }

  return [min, max];
}

/**
 * Calculate blocked spots for all spots on a ramp with detailed logging
 *
 * Algorithm:
 * 1. For each spot A with an egress lane:
 *    - Draw corridor from spot center to nearest point on egress lane
 *    - Corridor width = spot's max dimension
 *    - Find all spots B whose polygons intersect this corridor
 *    - Add spot A's ID to spot B's blocksSpotIds
 *
 * 2. If a spot has no egress lane, it has no blockers
 *
 * @param shapes - All shapes from the ramp
 * @returns Calculation result with updated shapes, logs, and statistics
 */
export function calculateBlockedSpots(
  shapes: Shape[]
): EgressCalculationResult {
  const logs: EgressCalculationLog[] = [];
  let totalBlockingRelationships = 0;
  let spotsWithEgress = 0;
  let spotsWithoutEgress = 0;

  // 1. Create new array and initialize all spots with empty egressOptions
  const updatedShapes = shapes.map((s) => {
    if (s.type === "Spot") {
      return {
        ...s,
        metadata: {
          ...s.metadata,
          blocksSpotIds: [],
          egressOptions: [], // Initialize empty for all spots
        } as unknown as SpotMetadata,
      };
    }
    return s;
  });

  // 2. Get spots and egress lanes
  const spots = updatedShapes.filter((s) => s.type === "Spot");
  const lanes = updatedShapes.filter((s) => s.tags?.includes("egress_lane"));

  // 3. For each spot with egress lane, find which spots it would be blocked by
  // Key insight: If spotA's corridor to egress intersects spotB, then spotB BLOCKS spotA
  // (not the other way around - spotB is in the way of spotA's departure)

  // Track which spots have egress lanes for logging
  const spotsWithEgressLanes = new Set<string>();

  // Build a map of which spots each lane serves (for consolidation later)
  type BlockingEntry = {
    blockedSpotId: string;
    laneId: string;
    laneName: string;
  };
  const blockingMap = new Map<string, BlockingEntry[]>(); // blocker spotId -> list of spots it blocks

  for (const spotA of spots) {
    const metadata = spotA.metadata as SpotMetadata;
    const egressLanes = metadata?.egressLanes || [];
    const spotName = spotA.text || `Spot ${spotA.id.slice(0, 8)}`;

    // Skip if no egress lane defined
    if (egressLanes.length === 0) {
      spotsWithoutEgress++;
      continue;
    }

    spotsWithEgress++;
    spotsWithEgressLanes.add(spotA.id);

    // For each egress lane option that spotA has
    for (const laneId of egressLanes) {
      const lane = lanes.find((l) => l.id === laneId);
      if (!lane) {
        console.warn(`Lane ${laneId} not found for spot ${spotName}`);
        continue;
      }

      const laneName = lane.text || `Lane ${laneId.slice(0, 8)}`;

      // Calculate corridor from spotA to this lane
      const centerA = getSpotCenter(spotA);
      const maxWidth = getSpotMaxWidth(spotA);
      const nearestPoint = nearestPointOnPolyline(centerA, lane.points);
      const corridor = createCorridor(centerA, nearestPoint, maxWidth);

      // Check which spots this corridor intersects
      // If spotA's corridor intersects spotB, then spotB BLOCKS spotA
      for (const spotB of spots) {
        if (spotB.id === spotA.id) {
          continue; // Skip self
        }

        // Skip if spotA is a gap spot that requires spotB to be occupied
        // WHY: Gap spots sit in the physical space BETWEEN their required spots
        // EXAMPLE: B06 sits between B01 and B02. When aircraft at B01 departs, it doesn't
        //          block B06 because B06 is positioned in the gap, not on the egress path.
        // LOGIC: If spotA requires spotB to be occupied, then spotB cannot block spotA
        //        (they have a dependency relationship, not a blocking relationship)
        const requiresOccupiedSpots = metadata.requiresOccupiedSpots || [];
        if (requiresOccupiedSpots.includes(spotB.id)) {
          continue; // Gap spot dependency, not a blocking relationship
        }

        if (polygonsIntersect(corridor, spotB.points)) {
          // spotB blocks spotA (spotB is in the way of spotA's egress)
          // Store this in spotB's blocking list
          if (!blockingMap.has(spotB.id)) {
            blockingMap.set(spotB.id, []);
          }
          blockingMap.get(spotB.id)!.push({
            blockedSpotId: spotA.id,
            laneId,
            laneName,
          });
          totalBlockingRelationships++;
        }
      }
    }
  }

  // 4. Consolidate blocking entries into egressOptions for each spot
  // Group by laneId to create the proper egressOptions structure
  for (const spot of spots) {
    const blockingEntries = blockingMap.get(spot.id) || [];

    if (blockingEntries.length === 0) {
      // No blocking relationships for this spot
      continue;
    }

    // Group by laneId
    const byLane = new Map<
      string,
      { laneName: string; blocksSpotIds: string[] }
    >();
    for (const entry of blockingEntries) {
      if (!byLane.has(entry.laneId)) {
        byLane.set(entry.laneId, {
          laneName: entry.laneName,
          blocksSpotIds: [],
        });
      }
      byLane.get(entry.laneId)!.blocksSpotIds.push(entry.blockedSpotId);
    }

    // Convert to egressOptions array
    const egressOptions: {
      laneId: string;
      laneName: string;
      blocksSpotIds: string[];
    }[] = [];
    for (const [laneId, data] of byLane) {
      egressOptions.push({
        laneId,
        laneName: data.laneName,
        blocksSpotIds: data.blocksSpotIds,
      });
    }

    // Store in spot metadata
    spot.metadata = {
      ...spot.metadata,
      egressOptions,
    } as SpotMetadata;
  }

  // 5. Generate logs for display
  for (const spot of spots) {
    const metadata = spot.metadata as SpotMetadata;
    const spotName = spot.text || `Spot ${spot.id.slice(0, 8)}`;
    const egressOptions = metadata?.egressOptions || [];
    const hasEgressLane = spotsWithEgressLanes.has(spot.id);

    if (!hasEgressLane) {
      logs.push({
        spotId: spot.id,
        spotName,
        hasEgressLane: false,
        blocksSpots: [],
        skipped: true,
        reason: "No egress lane",
        intersectionCount: 0,
      });
      continue;
    }

    if (egressOptions.length === 0) {
      // Has egress lane but doesn't block anyone
      logs.push({
        spotId: spot.id,
        spotName,
        egressLane: "No blocking",
        hasEgressLane: true,
        blocksSpots: [],
        skipped: false,
        reason: "0 lane(s) calculated",
        intersectionCount: spots.length - 1,
      });
      continue;
    }

    // Collect all blocked spot IDs (union across lanes)
    const allBlockedSpotIds = new Set<string>();
    for (const option of egressOptions) {
      option.blocksSpotIds.forEach((id) => allBlockedSpotIds.add(id));
    }

    // Log with multi-lane summary
    const lanesSummary = egressOptions
      .map((opt) => `${opt.laneName} (${opt.blocksSpotIds.length})`)
      .join(", ");

    logs.push({
      spotId: spot.id,
      spotName,
      egressLane: lanesSummary,
      hasEgressLane: true,
      blocksSpots: Array.from(allBlockedSpotIds),
      skipped: false,
      reason: `${egressOptions.length} lane(s) calculated`,
      intersectionCount: spots.length - 1,
    });
  }

  return {
    shapes: updatedShapes,
    logs,
    totalSpots: spots.length,
    spotsWithEgress,
    spotsWithoutEgress,
    totalBlockingRelationships,
  };
}

/**
 * Assign spots to zones based on spatial containment
 * Each spot is assigned to the zone whose polygon contains its center point
 *
 * @param shapes - All shapes from the ramp
 * @returns Updated shapes with spot.metadata.zoneId set
 */
export function assignSpotsToZones(shapes: Shape[]): Shape[] {
  const zones = shapes.filter((s) => s.type === "Zone");

  return shapes.map((shape) => {
    if (shape.type !== "Spot") {
      return shape;
    }

    // Find zone that contains this spot's center point
    const spotCenter = getSpotCenter(shape);
    const containingZone = zones.find((zone) =>
      pointInPolygon(spotCenter, zone.points)
    );

    if (containingZone) {
      return {
        ...shape,
        metadata: {
          ...shape.metadata,
          zoneId: containingZone.id,
        } as SpotMetadata,
      };
    }

    // No zone contains this spot - clear zoneId if it had one
    return {
      ...shape,
      metadata: {
        ...shape.metadata,
        zoneId: undefined,
      } as SpotMetadata,
    };
  });
}

/**
 * Calculate automatic names for spots based on zone assignment and position
 *
 * Algorithm:
 * 1. First assigns zones using assignSpotsToZones()
 * 2. Groups spots by zone
 * 3. Within each zone, sorts by Y position (top-to-bottom), then X position (left-to-right)
 * 4. Names spots as {zone_prefix}{number:02d} (e.g., A01, A02, A03)
 * 5. Spots without zones keep their existing names
 *
 * @param shapes - All shapes from the ramp
 * @returns Updated shapes with spot.text renamed
 */
export function calculateSpotNames(shapes: Shape[]): Shape[] {
  // First, assign zones
  const shapesWithZones = assignSpotsToZones(shapes);

  // Group spots by zone
  const spotsByZone = new Map<string, Shape[]>();
  const spotsWithoutZone: Shape[] = [];

  shapesWithZones.forEach((shape) => {
    if (shape.type !== "Spot") return;

    const metadata = shape.metadata as SpotMetadata;
    const zoneId = metadata?.zoneId;

    if (zoneId) {
      if (!spotsByZone.has(zoneId)) {
        spotsByZone.set(zoneId, []);
      }
      spotsByZone.get(zoneId)!.push(shape);
    } else {
      spotsWithoutZone.push(shape);
    }
  });

  // For each zone, sort spots and assign names
  const updatedShapes = shapesWithZones.map((shape) => {
    if (shape.type !== "Spot") return shape;

    const metadata = shape.metadata as SpotMetadata;
    const zoneId = metadata?.zoneId;

    if (!zoneId) {
      // Spots without zones get sequential "?" prefix names
      // Find this spot's index among all spots without zones
      const sortedSpotsWithoutZone = [...spotsWithoutZone].sort((a, b) => {
        const topLeftA = {
          x: Math.min(...a.points.map((p) => p.x)),
          y: Math.min(...a.points.map((p) => p.y)),
        };
        const topLeftB = {
          x: Math.min(...b.points.map((p) => p.x)),
          y: Math.min(...b.points.map((p) => p.y)),
        };

        if (Math.abs(topLeftA.y - topLeftB.y) > 10) {
          return topLeftA.y - topLeftB.y;
        }
        return topLeftA.x - topLeftB.x;
      });

      const spotIndex = sortedSpotsWithoutZone.findIndex(
        (s) => s.id === shape.id
      );
      if (spotIndex !== -1) {
        const spotNumber = spotIndex + 1;
        const newName = `?${spotNumber.toString().padStart(2, "0")}`;
        return {
          ...shape,
          text: newName,
        };
      }

      return shape;
    }

    const zone = shapesWithZones.find(
      (s) => s.id === zoneId && s.type === "Zone"
    );
    if (!zone) {
      return shape;
    }

    // Get zone prefix from zone.text (fallback to "Z")
    const zonePrefix = zone.text?.trim() || "Z";

    // Get all spots in this zone and sort them
    const spotsInZone = spotsByZone.get(zoneId) || [];

    // Sort by Y (top to bottom), then X (left to right)
    // Use top-left corner for natural reading order
    const sortedSpots = [...spotsInZone].sort((a, b) => {
      const topLeftA = {
        x: Math.min(...a.points.map((p) => p.x)),
        y: Math.min(...a.points.map((p) => p.y)),
      };
      const topLeftB = {
        x: Math.min(...b.points.map((p) => p.x)),
        y: Math.min(...b.points.map((p) => p.y)),
      };

      // Primary sort: Y position (top to bottom)
      if (Math.abs(topLeftA.y - topLeftB.y) > 10) {
        return topLeftA.y - topLeftB.y;
      }

      // Secondary sort: X position (left to right)
      return topLeftA.x - topLeftB.x;
    });

    // Find this spot's index in the sorted list
    const spotIndex = sortedSpots.findIndex((s) => s.id === shape.id);
    if (spotIndex === -1) {
      return shape;
    }

    // Generate name: {prefix}{number:02d}
    const spotNumber = spotIndex + 1;
    const newName = `${zonePrefix}${spotNumber.toString().padStart(2, "0")}`;

    return {
      ...shape,
      text: newName,
    };
  });

  return updatedShapes;
}

/**
 * Calculate the geometry of a spot including its rotation and dimensions
 * @param spot - The spot shape to analyze
 * @returns Geometry information including width, height, angle, and center
 */
export function getSpotGeometry(spot: Shape): {
  width: number;
  height: number;
  angle: number;
  center: Point;
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  if (!spot.points || spot.points.length < 2) {
    return {
      width: 0,
      height: 0,
      angle: 0,
      center: { x: 0, y: 0 },
      minX: 0,
      minY: 0,
      maxX: 0,
      maxY: 0,
    };
  }

  // Calculate bounding box
  const xs = spot.points.map((p) => p.x);
  const ys = spot.points.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  const width = maxX - minX;
  const height = maxY - minY;
  const center = getSpotCenter(spot);

  // Calculate rotation angle from first edge
  const angle = Math.atan2(
    spot.points[1].y - spot.points[0].y,
    spot.points[1].x - spot.points[0].x
  );

  return {
    width,
    height,
    angle,
    center,
    minX,
    minY,
    maxX,
    maxY,
  };
}

/**
 * Tile a spot in a grid pattern, maintaining its rotation and orientation
 * Creates a rows × cols grid of spots with proper spacing
 *
 * @param sourceSpot - The spot to tile
 * @param rows - Number of rows in the grid
 * @param cols - Number of columns in the grid
 * @param spacing - Gap between spots in pixels (default: 10)
 * @param rampId - Ramp ID for new spots
 * @param fboId - FBO ID for new spots
 * @returns Array of tiled spots
 */
export function tileSpot(
  sourceSpot: Shape,
  rows: number,
  cols: number,
  spacing: number = 0,
  rampId: string,
  fboId: string
): Shape[] {
  const geometry = getSpotGeometry(sourceSpot);
  const { width, height, angle } = geometry;
  const tiledSpots: Shape[] = [];

  // Generate UUID function
  const generateId = () => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  };

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      // Skip (0,0) to avoid creating a duplicate at the source spot's position
      if (row === 0 && col === 0) {
        continue;
      }

      // Calculate offset in the spot's local coordinate system
      const localOffsetX = col * (width + spacing);
      const localOffsetY = row * (height + spacing);

      // Rotate the offset to match the spot's orientation
      const offsetX =
        localOffsetX * Math.cos(angle) - localOffsetY * Math.sin(angle);
      const offsetY =
        localOffsetX * Math.sin(angle) + localOffsetY * Math.cos(angle);

      // Create new spot with transformed points
      const newPoints = sourceSpot.points.map((p) => ({
        x: p.x + offsetX,
        y: p.y + offsetY,
      }));

      tiledSpots.push({
        ...sourceSpot,
        id: generateId(),
        ramp_id: rampId,
        fbo_id: fboId,
        points: newPoints,
        metadata: {
          ...sourceSpot.metadata,
          // Clear gap spot metadata - tiled spots need gap detection re-run
          requiresOccupiedSpots: undefined,
        } as SpotMetadata,
      });
    }
  }

  return tiledSpots;
}
